import { Link as RouterLink } from 'react-router-dom'
import { cn } from '@cloudflare/kumo'
import { CalendarPlusIcon, PrinterIcon, UserPlusIcon, WifiHighIcon } from '@phosphor-icons/react'
import type { Icon } from '@phosphor-icons/react'
import { useTranslation } from '../../i18n'
import { onPlainClick } from './plain-click'

interface QuickAction {
  id: 'book' | 'invite' | 'wifi' | 'print'
  to: string
  labelKey: string
  Icon: Icon
  primary?: boolean
  /** Also in the phone tab bar (WiFi tab, Print under More): hidden there. */
  inTabBar?: boolean
}

// Wi-Fi and Print are in every role's "My space" nav (Layout.tsx), so all
// roles that reach this page get all four.
const ACTIONS: QuickAction[] = [
  {
    id: 'book',
    to: '/my/bookings/new',
    labelKey: 'dashboard.quickActions.bookRoom',
    Icon: CalendarPlusIcon,
    primary: true,
  },
  {
    id: 'invite',
    to: '/my/guests/new',
    labelKey: 'dashboard.quickActions.inviteGuest',
    Icon: UserPlusIcon,
  },
  {
    id: 'wifi',
    to: '/my/wifi',
    labelKey: 'dashboard.quickActions.wifi',
    Icon: WifiHighIcon,
    inTabBar: true,
  },
  {
    id: 'print',
    to: '/my/print',
    labelKey: 'dashboard.quickActions.print',
    Icon: PrinterIcon,
    inTabBar: true,
  },
]

export interface QuickActionsProps {
  /** Open the Book sheet instead of navigating (the link stays the fallback). */
  onBookRoom?: () => void
  /** Open the Invite sheet instead of navigating. */
  onInviteGuest?: () => void
}

/** 2×2 grid of the most common jobs (Book + Invite only above the phone tab bar). */
export function QuickActions({ onBookRoom, onInviteGuest }: QuickActionsProps = {}) {
  const { t } = useTranslation()
  const handlers: Partial<Record<QuickAction['id'], () => void>> = {
    book: onBookRoom,
    invite: onInviteGuest,
  }
  return (
    <nav aria-label={t('dashboard.quickActions.title')}>
      <ul className="home-quick-actions">
        {ACTIONS.map(({ id, to, labelKey, Icon, primary, inTabBar }) => (
          <li key={to} className={cn(inTabBar && 'home-quick-actions__in-tab-bar')}>
            <RouterLink
              to={to}
              onClick={onPlainClick(handlers[id])}
              className={cn('home-quick-action', primary && 'home-quick-action--primary')}
            >
              <Icon size={24} aria-hidden />
              <span>{t(labelKey)}</span>
            </RouterLink>
          </li>
        ))}
      </ul>
    </nav>
  )
}
